import OpportunityPreview from './OpportunityPreview';
import { crmRequest } from './crmApi';
import CrmView from './CrmView';
import { canUseCrm, canUseSalesAgenda, isSupplierDirectoryOnly } from './crmAccess';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabPanel, TabView } from 'primereact/tabview';
import { toast } from 'react-toastify';
import AddSellerDialog from './components/AddSellerDialog';
import AgendaReviewDialog from './components/AgendaReviewDialog';
import AgendaHeader from './components/AgendaHeader';
import AgendaStatusBanner from './components/AgendaStatusBanner';
import CancelVisitDialog from './components/CancelVisitDialog';
import DailyAgendaView from './components/DailyAgendaView';
import LocationCaptureDialog from './components/LocationCaptureDialog';
import OfficeActivityDialog from './components/OfficeActivityDialog';
import SalesSettingsView from './components/SalesSettingsView';
import SalesAgentsView from './components/SalesAgentsView';
import TeamAgendaView from './components/TeamAgendaView';
import UnplannedVisitDialog from './components/UnplannedVisitDialog';
import ReportVisitDialog from './components/ReportVisitDialog';
import VisitDialog from './components/VisitDialog';
import WeeklyAgendaView from './components/WeeklyAgendaView';
import {
    assignSellerProfile,
    cancelVisit,
    createAgenda,
    createOfficeActivity,
    createVisit,
    finishOfficeActivity,
    finishVisit,
    getAgendaByWeek,
    getSalesAgents,
    getSalesCustomers,
    getSalesSettings,
    getSellerCandidates,
    getTeamAgendaByWeek,
    getVisitReport,
    getWeeklyOverview,
    resetSalesSettings,
    reviewAgenda,
    removeSellerProfile,
    saveLiveMeeting,
    saveVisitReport,
    startOfficeActivity,
    startUnplannedOfficeActivity,
    startUnplannedVisit,
    startVisit,
    submitAgenda,
    submitVisitReport,
    updateOfficeActivity,
    updateSalesSettings,
    updateVisit
} from './salesApi';
import {
    addDays,
    addWeeks,
    formatFullDate,
    formatWeekRange,
    getInitialAgendaDate,
    getPanamaToday,
    getPanamaTodayKey,
    getVisibleWeekDays,
    getWeekEndKey,
    getWeekStartKey,
    isCommercialActivityDay,
    isCurrentWeek,
    isFutureWeek,
    isPastWeek,
    isReportWeekLocked,
    sortVisits
} from './utils/agendaDates';
import { didCaptureLocation, requestStartLocation } from './utils/geolocation';
import { createExecutionRequestId, executeWithNetworkRetry } from './utils/executionRetry';
import { getWeekdayLabel, mergeSalesSettings } from './utils/salesSettings';
import './ventas.css';

const SELLER_ROLE_UID = 'seller';

const getFallbackCapabilities = (roleUid) => ({
    viewTeamAgenda: ['sales', 'admin', 'superAdmin'].includes(roleUid),
    manageSellerAgendas: ['admin', 'superAdmin'].includes(roleUid),
    reviewAgendas: ['admin', 'superAdmin'].includes(roleUid),
    manageSellers: ['admin', 'superAdmin'].includes(roleUid),
    viewAllLocations: ['sales', 'admin', 'superAdmin'].includes(roleUid),
    viewAllReports: ['sales', 'admin', 'superAdmin'].includes(roleUid),
    manageSettings: ['admin', 'superAdmin'].includes(roleUid),
    managePermissions: roleUid === 'superAdmin'
});

const getCurrentAdmin = () => {
    const stored = JSON.parse(localStorage.getItem('userDetails') || localStorage.getItem('adminUser') || '{}');
    return stored?.data?.adminUser || stored?.data?.user || {};
};

const getErrorMessage = (errorOrFallback, fallback) => {
    if (typeof errorOrFallback === 'string') return errorOrFallback;
    return errorOrFallback?.response?.data?.message || errorOrFallback?.data?.message || fallback || 'No pudimos completar la acción. Revisa la conexión e intenta nuevamente.';
};

const VentasModule = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routeLocation = useLocation();
    const notificationOpportunity = new URLSearchParams(routeLocation.search).get('opportunity');
    const [notificationMetadata, setNotificationMetadata] = useState(null);
    useEffect(() => { let active = true; if (/^[a-f0-9]{24}$/i.test(notificationOpportunity || '')) crmRequest('/metadata').then(value => active && setNotificationMetadata(value)).catch(() => active && setNotificationMetadata({})); return () => { active = false; }; }, [notificationOpportunity]);
    const notificationPreview = notificationOpportunity && notificationMetadata ? <OpportunityPreview id={notificationOpportunity} metadata={notificationMetadata} onClose={() => navigate('/ventas', { replace: true })} onChanged={() => {}} /> : null;
    const [viewMode, setViewMode] = useState('week');
    const [selectedDate, setSelectedDate] = useState(() => getInitialAgendaDate());
    const [agenda, setAgenda] = useState(null);
    const [loadingAgenda, setLoadingAgenda] = useState(false);
    const [savingVisit, setSavingVisit] = useState(false);
    const [cancellingVisit, setCancellingVisit] = useState(false);
    const [submittingAgenda, setSubmittingAgenda] = useState(false);
    const [startingVisitId, setStartingVisitId] = useState(null);
    const [finishingVisitId, setFinishingVisitId] = useState(null);
    const [savingReportVisitId, setSavingReportVisitId] = useState(null);
    const [loadingReport, setLoadingReport] = useState(false);
    const [startingUnplannedVisit, setStartingUnplannedVisit] = useState(false);
    const [visitDialogVisible, setVisitDialogVisible] = useState(false);
    const [officeDialogVisible, setOfficeDialogVisible] = useState(false);
    const [officeDialogMode, setOfficeDialogMode] = useState('planned');
    const [editingOfficeActivity, setEditingOfficeActivity] = useState(null);
    const [savingOfficeActivity, setSavingOfficeActivity] = useState(false);
    const [reportDialogVisible, setReportDialogVisible] = useState(false);
    const [reportVisit, setReportVisit] = useState(null);
    const [reportContext, setReportContext] = useState('my');
    const [unplannedDialogVisible, setUnplannedDialogVisible] = useState(false);
    const [editingVisit, setEditingVisit] = useState(null);
    const [visitContext, setVisitContext] = useState('my');
    const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
    const [visitToCancel, setVisitToCancel] = useState(null);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [agents, setAgents] = useState([]);
    const [loadingAgents, setLoadingAgents] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [customerLoadError, setCustomerLoadError] = useState('');
    const [teamAgents, setTeamAgents] = useState([]);
    const [loadingTeamAgents, setLoadingTeamAgents] = useState(false);
    const [sellerDialogVisible, setSellerDialogVisible] = useState(false);
    const [sellerCandidates, setSellerCandidates] = useState([]);
    const [loadingSellerCandidates, setLoadingSellerCandidates] = useState(false);
    const [assigningSeller, setAssigningSeller] = useState(false);
    const [deletingSellerId, setDeletingSellerId] = useState('');
    const [managedAgentId, setManagedAgentId] = useState('');
    const [teamAgenda, setTeamAgenda] = useState(null);
    const [teamSelectedAgentId, setTeamSelectedAgentId] = useState('');
    const [teamSelectedDate, setTeamSelectedDate] = useState(() => getInitialAgendaDate());
    const [teamViewMode, setTeamViewMode] = useState('week');
    const [loadingTeamAgenda, setLoadingTeamAgenda] = useState(false);
    const [teamOverview, setTeamOverview] = useState(null);
    const [loadingTeamOverview, setLoadingTeamOverview] = useState(false);
    const [reviewDialogVisible, setReviewDialogVisible] = useState(false);
    const [reviewingAgenda, setReviewingAgenda] = useState(false);
    const [settingsState, setSettingsState] = useState(null);
    const [loadingSettings, setLoadingSettings] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [locationDecision, setLocationDecision] = useState(null);
    const locationDecisionResolver = useRef(null);
    const currentAdmin = useMemo(() => getCurrentAdmin(), []);
    const currentUserId = currentAdmin?._id;
    const roleUid = currentAdmin?.role?.uid;
    const canAccessAgenda = canUseSalesAgenda(currentAdmin);
    const canAccessCrm = canUseCrm(currentAdmin);
    const capabilities = settingsState?.capabilities || getFallbackCapabilities(roleUid);
    const activeSalesSettings = useMemo(
        () => mergeSalesSettings(settingsState?.activeConfig),
        [settingsState?.activeConfig]
    );

    const requestLocationDecision = useCallback((decision) => new Promise((resolve) => {
        locationDecisionResolver.current = resolve;
        setLocationDecision(decision);
    }), []);

    const resolveLocationDecision = useCallback((result) => {
        const resolve = locationDecisionResolver.current;
        locationDecisionResolver.current = null;
        setLocationDecision(null);
        resolve?.(result);
    }, []);

    const captureExecutionLocation = useCallback(async (phase) => {
        const locationSettings = activeSalesSettings.location;
        while (true) {
            const payload = await requestStartLocation(locationSettings.gpsTimeoutMs);
            if (didCaptureLocation(payload)) {
                if (Number(payload.location.accuracyMeters) <= locationSettings.accuracyThresholdMeters) return payload;
                const decision = await requestLocationDecision({
                    kind: 'approximate',
                    phase,
                    payload,
                    accuracyThresholdMeters: locationSettings.accuracyThresholdMeters
                });
                if (decision?.action === 'retry') continue;
                if (decision?.action === 'continue') return payload;
                return null;
            }

            const decision = await requestLocationDecision({
                kind: 'unavailable',
                phase,
                payload,
                required:
                    (phase === 'finish' ? locationSettings.requireEndLocation : locationSettings.requireStartLocation) ||
                    !locationSettings.allowWithoutLocationWithJustification,
                minimumExceptionNoteLength: locationSettings.minimumExceptionNoteLength,
                gpsTimeoutMs: locationSettings.gpsTimeoutMs
            });
            if (decision?.action === 'retry') continue;
            if (decision?.action === 'continue') {
                return {
                    ...payload,
                    locationUnavailableNote: decision.note
                };
            }
            return null;
        }
    }, [activeSalesSettings.location, requestLocationDecision]);

    useEffect(() => () => {
        locationDecisionResolver.current?.(null);
        locationDecisionResolver.current = null;
    }, []);

    const isAdmin = capabilities.manageSellerAgendas;
    const isSeller = roleUid === SELLER_ROLE_UID;
    const canViewAllReports = capabilities.viewAllReports;
    const canManageSellers = capabilities.manageSellers;
    const canReviewAgendas = capabilities.reviewAgendas;
    const canManageSettings = settingsState?.canManageSettings ??
        ['admin', 'superAdmin'].includes(roleUid);
    const showMyAgenda = isSeller || isAdmin;
    const canViewTeamAgenda = capabilities.viewTeamAgenda;
    const canViewAgents = capabilities.viewTeamAgenda || canManageSellers;
    const managedAgent = useMemo(() => agents.find((agent) => agent._id === managedAgentId) || null, [agents, managedAgentId]);
    const managedAgendaAgentId = isAdmin
        ? (managedAgentId || (isSeller ? currentUserId : ''))
        : (canAccessAgenda ? currentUserId : '');
    const selectedWeekStart = useMemo(() => getWeekStartKey(selectedDate), [selectedDate]);
    const selectedWeekEnd = useMemo(() => getWeekEndKey(selectedDate, activeSalesSettings), [activeSalesSettings, selectedDate]);
    const visits = useMemo(() => sortVisits(agenda?.visits || []), [agenda]);
    const activeVisitCount = visits.filter((visit) => visit?.planningStatus !== 'cancelled' && visit?.visitOrigin !== 'unplanned').length;
    const hasActiveVisit = visits.some((visit) => visit?.executionStatus === 'inProgress' && String(visit?.agent?._id || visit?.agent || '') === String(currentUserId || ''));
    const selectedWeekIsPast = isPastWeek(selectedDate);
    const selectedWeekIsCurrent = isCurrentWeek(selectedDate);
    const selectedWeekIsFuture = isFutureWeek(selectedDate);
    const planningDeadlineLabel = `${getWeekdayLabel(activeSalesSettings.planning.submissionDeadlineWeekday)} a las ${activeSalesSettings.planning.submissionDeadlineTime}`;
    const canManageSellerWeek = isSeller && (
        agenda?.reviewStatus === 'returned' ||
        selectedWeekIsFuture
    );
    const canManageAdminWeek = Boolean(
        isAdmin &&
        managedAgentId &&
        (
            selectedWeekIsFuture ||
            (selectedWeekIsCurrent && activeSalesSettings.planning.adminOverride)
        )
    );
    const canManageWeek = !selectedWeekIsPast && (
        canManageAdminWeek ||
        ((!agenda || agenda.status === 'draft') && canManageSellerWeek)
    );
    const canCreateVisit = canManageWeek;
    const canEditVisits = canManageWeek;
    const canSubmitAgenda = Boolean(agenda?._id && agenda.status === 'draft' && activeVisitCount > 0 && canManageWeek);
    const visibleWeekDays = useMemo(() => getVisibleWeekDays(selectedDate, activeSalesSettings), [activeSalesSettings, selectedDate]);
    const teamSelectedWeekStart = useMemo(() => getWeekStartKey(teamSelectedDate), [teamSelectedDate]);
    const teamSelectedWeekEnd = useMemo(() => getWeekEndKey(teamSelectedDate, activeSalesSettings), [activeSalesSettings, teamSelectedDate]);
    const teamVisits = useMemo(() => sortVisits(teamAgenda?.visits || []), [teamAgenda]);
    const teamActiveVisitCount = teamVisits.filter((visit) => visit?.planningStatus !== 'cancelled' && visit?.visitOrigin !== 'unplanned').length;
    const teamWeekIsPast = isPastWeek(teamSelectedDate);
    const teamWeekIsCurrent = isCurrentWeek(teamSelectedDate);
    const teamWeekIsFuture = isFutureWeek(teamSelectedDate);
    const teamVisibleWeekDays = useMemo(() => getVisibleWeekDays(teamSelectedDate, activeSalesSettings), [activeSalesSettings, teamSelectedDate]);
    const canManageTeamWeek = Boolean(
        isAdmin &&
        teamSelectedAgentId &&
        !teamWeekIsPast &&
        (
            teamWeekIsFuture ||
            (teamWeekIsCurrent && activeSalesSettings.planning.adminOverride)
        )
    );
    const isReportLockedForSettings = useCallback(
        (visit) => isReportWeekLocked(visit, activeSalesSettings),
        [activeSalesSettings]
    );

    const dateLabel = useMemo(() => (viewMode === 'day' ? formatFullDate(selectedDate) : formatWeekRange(selectedDate, activeSalesSettings)), [activeSalesSettings, selectedDate, viewMode]);
    const teamDateLabel = useMemo(() => (teamViewMode === 'day' ? formatFullDate(teamSelectedDate) : formatWeekRange(teamSelectedDate, activeSalesSettings)), [activeSalesSettings, teamSelectedDate, teamViewMode]);
    const todayKey = getPanamaTodayKey();
    const canEditPlannedVisit = (visit, context = 'my') => {
        const contextAllowed = context === 'team' ? canManageTeamWeek : canEditVisits;
        if (!contextAllowed || visit?.planningStatus === 'cancelled') return false;
        if ((visit?.executionStatus || 'notStarted') !== 'notStarted') return false;
        if (isAdmin) return Boolean(visit?.plannedDate > todayKey);
        return true;
    };
    const showImmediateActions = Boolean(
        isSeller && (!isAdmin || String(managedAgendaAgentId) === String(currentUserId || ''))
    );
    const canStartImmediateActivity = Boolean(
        showImmediateActions &&
        isCommercialActivityDay(activeSalesSettings) &&
        !hasActiveVisit
    );
    const immediateActivityDisabledReason = !isCommercialActivityDay(activeSalesSettings)
        ? 'Disponible únicamente en los días comerciales configurados.'
        : hasActiveVisit
            ? 'Ya tienes una actividad en progreso. Finalízala antes de iniciar otra.'
            : '';
    const newVisitDisabledReason = isAdmin && !managedAgentId
        ? 'Selecciona un vendedor para gestionar su agenda'
        : selectedWeekIsPast
            ? 'No se pueden crear visitas en semanas cerradas'
            : agenda?.status === 'submitted' && !isAdmin
                ? 'La agenda enviada no permite cambios'
                : isAdmin && selectedWeekIsCurrent && !activeSalesSettings.planning.adminOverride
                    ? 'Los ajustes administrativos de la semana actual están desactivados'
                : selectedWeekIsCurrent && !isAdmin
                    ? 'Selecciona una semana futura para planificar nuevas citas'
                    : !canCreateVisit
                    ? 'Esta agenda no está disponible para cambios'
                    : '';

    const loadSettings = useCallback(async () => {
        if (!canAccessAgenda) return;
        setLoadingSettings(true);
        try {
            const response = await getSalesSettings(dispatch);
            if (response?.data) setSettingsState(response.data);
        } catch (error) {
            toast.error(getErrorMessage(error, 'No pudimos cargar los ajustes de Ventas.'));
        } finally {
            setLoadingSettings(false);
        }
    }, [dispatch, canAccessAgenda]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleSaveSettings = async (payload) => {
        if (!canManageSettings || savingSettings) return;
        setSavingSettings(true);
        try {
            const response = await updateSalesSettings(payload, dispatch);
            if (!response || response.status >= 400) return;
            setSettingsState(response?.data?.data || null);
            toast.success(payload.applyMode === 'immediate'
                ? 'Ajustes aplicados.'
                : 'Ajustes programados para el próximo lunes.');
            await Promise.all([loadAgenda(), loadTeamAgenda(), loadTeamOverview()]);
        } catch (error) {
            toast.error(getErrorMessage(error, 'No pudimos guardar los ajustes.'));
        } finally {
            setSavingSettings(false);
        }
    };

    const handleResetSettings = async (payload) => {
        if (!settingsState?.canManagePermissions || savingSettings) return;
        setSavingSettings(true);
        try {
            const response = await resetSalesSettings(payload, dispatch);
            if (!response || response.status >= 400) return;
            setSettingsState(response?.data?.data || null);
            toast.success(payload.applyMode === 'immediate'
                ? 'Valores predeterminados restaurados.'
                : 'Restauración programada para el próximo lunes.');
            await Promise.all([loadAgenda(), loadTeamAgenda(), loadTeamOverview()]);
        } catch (error) {
            toast.error(getErrorMessage(error, 'No pudimos restaurar los ajustes.'));
        } finally {
            setSavingSettings(false);
        }
    };

    const loadAgenda = useCallback(async () => {
        if (!selectedWeekStart || !managedAgendaAgentId) {
            setAgenda(null);
            return;
        }

        setLoadingAgenda(true);
        try {
            const response = await getAgendaByWeek(selectedWeekStart, isAdmin ? managedAgendaAgentId : undefined, dispatch);
            const nextAgenda = Array.isArray(response?.data) ? response.data[0] || null : null;
            setAgenda(nextAgenda);
        } catch (error) {
            toast.error(getErrorMessage('No pudimos cargar la agenda.'));
            setAgenda(null);
        } finally {
            setLoadingAgenda(false);
        }
    }, [dispatch, isAdmin, managedAgendaAgentId, selectedWeekStart]);

    useEffect(() => {
        loadAgenda();
    }, [loadAgenda]);

    const loadAgents = useCallback(async () => {
        if (!canViewTeamAgenda && !canViewAgents) return;
        setLoadingAgents(true);
        try {
            const response = await getSalesAgents(dispatch);
            const nextAgents = Array.isArray(response?.data) ? response.data : [];
            setAgents(nextAgents);
        } catch (error) {
            toast.error(getErrorMessage('No pudimos cargar los vendedores.'));
            setAgents([]);
        } finally {
            setLoadingAgents(false);
        }
    }, [canViewAgents, canViewTeamAgenda, dispatch]);

    useEffect(() => {
        loadAgents();
    }, [loadAgents]);

    useEffect(() => {
        if (
            isSeller &&
            isAdmin &&
            currentUserId &&
            !managedAgentId &&
            agents.some((agent) => String(agent._id) === String(currentUserId))
        ) {
            setManagedAgentId(String(currentUserId));
        }
    }, [agents, currentUserId, isAdmin, isSeller, managedAgentId]);

    const loadCustomers = useCallback(async () => {
        if (!canAccessAgenda) return;
        setLoadingCustomers(true);
        setCustomerLoadError('');
        try {
            const response = await getSalesCustomers(dispatch);
            if (!Array.isArray(response?.data)) {
                throw new Error('Customer catalog unavailable');
            }
            setCustomers(response.data);
        } catch (error) {
            setCustomers([]);
            setCustomerLoadError('No pudimos cargar los clientes de QuickBooks.');
        } finally {
            setLoadingCustomers(false);
        }
    }, [dispatch, canAccessAgenda]);

    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    useEffect(() => {
        if (visitDialogVisible || unplannedDialogVisible) loadCustomers();
    }, [visitDialogVisible, unplannedDialogVisible, loadCustomers]);

    const loadTeamAgents = useCallback(async () => {
        if (!canViewTeamAgenda || !teamSelectedWeekStart) {
            setTeamAgents([]);
            return;
        }
        setLoadingTeamAgents(true);
        try {
            const response = await getSalesAgents(dispatch, teamSelectedWeekStart);
            const nextAgents = Array.isArray(response?.data) ? response.data : [];
            setTeamAgents(nextAgents);
        } catch (error) {
            toast.error(getErrorMessage('No pudimos cargar los vendedores del equipo.'));
            setTeamAgents([]);
        } finally {
            setLoadingTeamAgents(false);
        }
    }, [canViewTeamAgenda, dispatch, teamSelectedWeekStart]);

    useEffect(() => {
        loadTeamAgents();
    }, [loadTeamAgents]);

    const loadTeamOverview = useCallback(async () => {
        if (!canViewTeamAgenda || !teamSelectedWeekStart) {
            setTeamOverview(null);
            return;
        }
        setLoadingTeamOverview(true);
        try {
            const response = await getWeeklyOverview(teamSelectedWeekStart, dispatch);
            setTeamOverview(response?.data || null);
        } catch (error) {
            toast.error(getErrorMessage(error, 'No pudimos cargar el resumen semanal.'));
            setTeamOverview(null);
        } finally {
            setLoadingTeamOverview(false);
        }
    }, [canViewTeamAgenda, dispatch, teamSelectedWeekStart]);

    useEffect(() => {
        loadTeamOverview();
    }, [loadTeamOverview]);

    useEffect(() => {
        if (!teamSelectedAgentId || loadingTeamAgents) return;
        const stillAvailable = teamAgents.some((agent) => agent._id === teamSelectedAgentId);
        if (!stillAvailable) {
            setTeamSelectedAgentId('');
            setTeamAgenda(null);
        }
    }, [loadingTeamAgents, teamAgents, teamSelectedAgentId]);

    const loadSellerCandidates = useCallback(async () => {
        if (!canManageSellers) return;
        setLoadingSellerCandidates(true);
        try {
            const response = await getSellerCandidates(dispatch);
            setSellerCandidates(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            toast.error(getErrorMessage('No pudimos cargar los usuarios elegibles.'));
            setSellerCandidates([]);
        } finally {
            setLoadingSellerCandidates(false);
        }
    }, [canManageSellers, dispatch]);

    useEffect(() => {
        if (!canManageSellers) return;
        loadSellerCandidates();
    }, [canManageSellers, loadSellerCandidates]);

    const handleOpenSellerDialog = () => {
        if (!canManageSellers) return;
        setSellerDialogVisible(true);
        loadSellerCandidates();
    };

    const handleCloseSellerDialog = () => {
        if (assigningSeller) return;
        setSellerDialogVisible(false);
    };

    const handleAssignSeller = async (payload) => {
        if (assigningSeller) return;
        setAssigningSeller(true);
        try {
            const response = await assignSellerProfile(payload, dispatch);
            if (!response || response.status >= 400) return;
            toast.success('Vendedor agregado. El usuario debe cerrar sesión y volver a entrar para actualizar su navegación.');
            setSellerDialogVisible(false);
            await Promise.all([loadAgents(), loadTeamAgents(), loadSellerCandidates()]);
        } catch (error) {
            toast.error(getErrorMessage('No pudimos agregar el vendedor.'));
        } finally {
            setAssigningSeller(false);
        }
    };

    const handleDeleteSeller = (agent) => {
        if (!canManageSellers || !agent?._id || deletingSellerId) return;
        confirmDialog({
            header: '¿Borrar vendedor?',
            message: `${agent.name || agent.email || 'Este vendedor'} perderá acceso al sistema y dejará de aparecer en la agenda. Su historial comercial se conservará.`,
            acceptLabel: 'Borrar vendedor',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                setDeletingSellerId(agent._id);
                try {
                    const response = await removeSellerProfile(agent._id, dispatch);
                    if (!response || response.status >= 400) return;
                    toast.success('Vendedor borrado. Su historial comercial se conservó.');
                    if (managedAgentId === agent._id) {
                        setManagedAgentId('');
                        setAgenda(null);
                    }
                    if (teamSelectedAgentId === agent._id) {
                        setTeamSelectedAgentId('');
                        setTeamAgenda(null);
                    }
                    await Promise.all([loadAgents(), loadTeamAgents(), loadTeamOverview(), loadSellerCandidates()]);
                } catch (error) {
                    toast.error(getErrorMessage(error, 'No pudimos borrar el vendedor.'));
                } finally {
                    setDeletingSellerId('');
                }
            }
        });
    };

    const handleCreateSellerInUsers = () => {
        setSellerDialogVisible(false);
        navigate('/user-list', { state: { intendedRoleUid: 'seller', returnTo: '/ventas' } });
    };

    const loadTeamAgenda = useCallback(async () => {
        if (!canViewTeamAgenda || !teamSelectedAgentId || !teamSelectedWeekStart) {
            setTeamAgenda(null);
            return;
        }

        setLoadingTeamAgenda(true);
        setTeamAgenda(null);
        try {
            const response = await getTeamAgendaByWeek(teamSelectedAgentId, teamSelectedWeekStart, dispatch);
            const nextAgenda = Array.isArray(response?.data) ? response.data[0] || null : null;
            setTeamAgenda(nextAgenda);
        } catch (error) {
            toast.error(getErrorMessage('No pudimos cargar la agenda del equipo.'));
            setTeamAgenda(null);
        } finally {
            setLoadingTeamAgenda(false);
        }
    }, [canViewTeamAgenda, dispatch, teamSelectedAgentId, teamSelectedWeekStart]);

    useEffect(() => {
        loadTeamAgenda();
    }, [loadTeamAgenda]);

    useEffect(() => {
        const teamTabIndex = showMyAgenda ? 1 : 0;
        if (activeTabIndex === teamTabIndex && !teamSelectedAgentId && managedAgentId) {
            setTeamSelectedAgentId(managedAgentId);
        }
    }, [activeTabIndex, managedAgentId, showMyAgenda, teamSelectedAgentId]);

    const handlePrevious = () => {
        setSelectedDate((current) => (viewMode === 'day' ? addDays(current, -1) : addWeeks(current, -1)));
    };

    const handleNext = () => {
        setSelectedDate((current) => (viewMode === 'day' ? addDays(current, 1) : addWeeks(current, 1)));
    };

    const handleToday = () => {
        setSelectedDate(getPanamaToday());
    };

    const handleOpenNewVisit = () => {
        if (isAdmin && !managedAgentId) {
            toast.warn('Selecciona un vendedor para gestionar su agenda.');
            return;
        }
        setEditingVisit(null);
        setVisitContext('my');
        setVisitDialogVisible(true);
    };

    const handleOpenTeamNewVisit = () => {
        if (!canManageTeamWeek) return;
        setEditingVisit(null);
        setVisitContext('team');
        setVisitDialogVisible(true);
    };

    const handleOpenOfficeActivity = (mode = 'planned', context = 'my', activity = null) => {
        if (mode === 'planned' && context === 'my' && isAdmin && !managedAgentId) {
            toast.warn('Selecciona un vendedor para gestionar su agenda.');
            return;
        }
        setOfficeDialogMode(mode);
        setVisitContext(context);
        setEditingOfficeActivity(activity);
        setOfficeDialogVisible(true);
    };

    const handleCloseOfficeDialog = () => {
        if (savingOfficeActivity) return;
        setOfficeDialogVisible(false);
        setEditingOfficeActivity(null);
        setOfficeDialogMode('planned');
    };

    const handleCloseVisitDialog = () => {
        if (savingVisit) return;
        setVisitDialogVisible(false);
        setEditingVisit(null);
    };

    const handleCloseUnplannedDialog = () => {
        if (startingUnplannedVisit) return;
        setUnplannedDialogVisible(false);
    };

    const ensureAgendaForVisit = async (context = 'my') => {
        if (context === 'team') {
            if (teamAgenda?._id) return teamAgenda;
            const response = await createAgenda(
                {
                    weekStartDate: teamSelectedWeekStart,
                    agentId: teamSelectedAgentId
                },
                dispatch
            );
            return response?.data?.data || null;
        }

        if (agenda?._id) return agenda;
        const response = await createAgenda(
            {
                weekStartDate: selectedWeekStart,
                ...(isAdmin ? { agentId: managedAgentId } : {})
            },
            dispatch
        );
        return response?.data?.data || null;
    };

    const persistVisit = async (payload) => {
        if (savingVisit) return;
        setSavingVisit(true);
        try {
            if (editingVisit?._id) {
                const response = await updateVisit(editingVisit._id, payload, dispatch);
                if (!response || response.status >= 400) return;
                toast.success('Visita actualizada.');
            } else {
                const targetAgenda = await ensureAgendaForVisit(visitContext);
                if (!targetAgenda?._id) return;
                const response = await createVisit(targetAgenda._id, payload, dispatch);
                if (!response || response.status >= 400) return;
                toast.success('Visita agregada a la agenda.');
            }
            setVisitDialogVisible(false);
            setEditingVisit(null);
            if (visitContext === 'team') {
                await Promise.all([loadTeamAgenda(), loadTeamOverview()]);
            } else {
                await loadAgenda();
                if (isAdmin && managedAgentId) setTeamSelectedAgentId(managedAgentId);
                if (canViewTeamAgenda) await Promise.all([loadTeamAgenda(), loadTeamOverview()]);
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'No pudimos guardar la visita.'));
        } finally {
            setSavingVisit(false);
        }
    };

    const handleSaveVisit = (payload) => {
        const isReschedule = Boolean(
            editingVisit?._id &&
            (
                editingVisit.plannedDate !== payload.plannedDate ||
                editingVisit.plannedStartTime !== payload.plannedStartTime ||
                editingVisit.plannedEndTime !== payload.plannedEndTime
            )
        );
        if (!isReschedule) {
            persistVisit(payload);
            return;
        }
        confirmDialog({
            header: '¿Reprogramar esta visita?',
            message: `El nuevo horario será ${payload.plannedDate}, de ${payload.plannedStartTime} a ${payload.plannedEndTime}. Se validará que no se superponga con otra visita.`,
            acceptLabel: 'Reprogramar',
            rejectLabel: 'Volver',
            acceptClassName: 'p-button-warning',
            accept: () => persistVisit(payload)
        });
    };

    const handleSaveOfficeActivity = async (payload) => {
        if (savingOfficeActivity) return;
        setSavingOfficeActivity(true);
        try {
            if (officeDialogMode === 'startNow') {
                const response = await startUnplannedOfficeActivity({
                    ...payload,
                    executionRequestId: createExecutionRequestId()
                }, dispatch);
                if (!response || response.status >= 400) return;
                toast.success('Tiempo de oficina iniciado. No se solicitará reporte ni GPS.');
            } else if (editingOfficeActivity?._id) {
                const response = await updateOfficeActivity(
                    editingOfficeActivity._id,
                    payload,
                    dispatch
                );
                if (!response || response.status >= 400) return;
                toast.success('Tiempo de oficina actualizado.');
            } else {
                const targetAgenda = await ensureAgendaForVisit(visitContext);
                if (!targetAgenda?._id) return;
                const response = await createOfficeActivity(
                    targetAgenda._id,
                    payload,
                    dispatch
                );
                if (!response || response.status >= 400) return;
                toast.success('Tiempo de oficina agregado a la agenda.');
            }
            setOfficeDialogVisible(false);
            setEditingOfficeActivity(null);
            setOfficeDialogMode('planned');
            if (visitContext === 'team') {
                await Promise.all([loadTeamAgenda(), loadTeamOverview()]);
            } else {
                await Promise.all([loadAgenda(), loadTeamOverview()]);
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'No pudimos guardar el tiempo de oficina.'));
        } finally {
            setSavingOfficeActivity(false);
        }
    };

    const handleEditVisit = (visit, context = 'my') => {
        const canEdit = context === 'team' ? canManageTeamWeek : canEditVisits;
        if (!canEdit || visit?.planningStatus === 'cancelled') return;
        if (visit?.activityType === 'office') {
            handleOpenOfficeActivity('planned', context, visit);
            return;
        }
        setEditingVisit(visit);
        setVisitContext(context);
        setVisitDialogVisible(true);
    };

    const handleOpenCancelVisit = (visit, context = 'my') => {
        const canEdit = context === 'team' ? canManageTeamWeek : canEditVisits;
        if (!canEdit || visit?.planningStatus === 'cancelled') return;
        setVisitToCancel(visit);
        setVisitContext(context);
        setCancelDialogVisible(true);
    };

    const handleConfirmCancelVisit = async (reason) => {
        if (!visitToCancel?._id || cancellingVisit) return;
        setCancellingVisit(true);
        try {
            const response = await cancelVisit(visitToCancel._id, reason, dispatch);
            if (!response || response.status >= 400) return;
            toast.success(visitToCancel?.activityType === 'office' ? 'Tiempo de oficina cancelado.' : 'Visita cancelada.');
            setCancelDialogVisible(false);
            setVisitToCancel(null);
            if (visitContext === 'team') {
                await Promise.all([loadTeamAgenda(), loadTeamOverview()]);
            } else {
                await loadAgenda();
                if (isAdmin && managedAgentId) setTeamSelectedAgentId(managedAgentId);
                if (canViewTeamAgenda) await Promise.all([loadTeamAgenda(), loadTeamOverview()]);
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'No pudimos cancelar la visita.'));
        } finally {
            setCancellingVisit(false);
        }
    };

    const handleSubmitAgenda = () => {
        if (!canSubmitAgenda || submittingAgenda) return;
        confirmDialog({
            header: '¿Enviar agenda semanal?',
            message: 'Después de enviarla no podrás agregar, editar ni cancelar las visitas planificadas.',
            acceptLabel: 'Enviar agenda',
            rejectLabel: 'Volver',
            acceptClassName: 'p-button-primary',
            accept: async () => {
                setSubmittingAgenda(true);
                try {
                    const response = await submitAgenda(agenda._id, dispatch);
                    if (!response || response.status >= 400) return;
                    toast.success('Agenda semanal enviada.');
                    await loadAgenda();
                    if (isAdmin && managedAgentId) setTeamSelectedAgentId(managedAgentId);
                    if (canViewTeamAgenda) await Promise.all([loadTeamAgenda(), loadTeamOverview()]);
                } catch (error) {
                    toast.error(getErrorMessage('No pudimos enviar la agenda.'));
                } finally {
                    setSubmittingAgenda(false);
                }
            }
        });
    };

    const canStartVisit = (visit) => {
        const visitAgentId = String(visit?.agent?._id || visit?.agent || '');
        return Boolean(
            isSeller &&
            agenda?.status === 'submitted' &&
            visitAgentId === String(currentUserId || '') &&
            visit?.visitOrigin !== 'unplanned' &&
            visit?.planningStatus === 'planned' &&
            (visit?.executionStatus || 'notStarted') === 'notStarted' &&
            visit?.plannedDate === todayKey &&
            !hasActiveVisit
        );
    };

    const canFinishVisit = (visit) => {
        const visitAgentId = String(visit?.agent?._id || visit?.agent || '');
        return Boolean(
            isSeller &&
            visitAgentId === String(currentUserId || '') &&
            visit?.executionStatus === 'inProgress'
        );
    };

    const hasReportContent = (visit) => {
        const report = visit?.report || {};
        return Boolean(
            report.summary ||
            report.outcome ||
            report.metWith ||
            report.identifiedNeeds ||
            report.productsOfInterest ||
            report.objections ||
            report.agreements ||
            report.nextSteps ||
            report.additionalComments ||
            Number(report.photoCount || 0) > 0 ||
            (Array.isArray(report.photos) && report.photos.length)
        );
    };

    const canReportVisit = (visit) => {
        if (visit?.activityType === 'office') return false;
        const visitAgentId = String(visit?.agent?._id || visit?.agent || '');
        const executionStatus = visit?.executionStatus || 'notStarted';
        const reportIsSubmitted = visit?.reportStatus === 'submitted';
        return Boolean(
            isSeller &&
            visitAgentId === String(currentUserId || '') &&
            visit?.planningStatus !== 'cancelled' &&
            ['inProgress', 'pendingReport', 'completed', 'notCompleted'].includes(executionStatus) &&
            (!reportIsSubmitted || activeSalesSettings.reports.allowSubmittedEditsUntilLock) &&
            !isReportLockedForSettings(visit)
        );
    };

    const canViewReport = (visit) => Boolean(
        visit?.activityType !== 'office' &&
        (
            ['inProgress', 'pendingReport', 'completed', 'notCompleted'].includes(visit?.executionStatus) ||
            hasReportContent(visit)
        )
    );

    const handleOpenReport = async (visit, context = 'my') => {
        if (!visit?._id) return;
        setReportVisit(visit);
        setReportContext(context);
        setReportDialogVisible(true);
        setLoadingReport(true);
        try {
            const response = await getVisitReport(visit._id, dispatch);
            const fullReport = response?.data || response?.data?.data;
            if (fullReport?._id) setReportVisit(fullReport);
        } catch (error) {
            toast.error(getErrorMessage('No pudimos cargar el reporte completo.'));
            setReportDialogVisible(false);
            setReportVisit(null);
            setReportContext('my');
        } finally {
            setLoadingReport(false);
        }
    };

    const handleCloseReportDialog = () => {
        if (savingReportVisitId || loadingReport) return;
        setReportDialogVisible(false);
        setReportVisit(null);
        setReportContext('my');
    };

    const refreshReportContext = async () => {
        if (reportContext === 'team') {
            await Promise.all([loadTeamAgenda(), loadTeamOverview()]);
        } else {
            await Promise.all([loadAgenda(), loadTeamOverview()]);
        }
    };

    const handleSaveReportDraft = async (payload) => {
        if (!reportVisit?._id || savingReportVisitId || !canReportVisit(reportVisit)) return;
        setSavingReportVisitId(reportVisit._id);
        try {
            const response = await saveVisitReport(reportVisit._id, { ...payload, reportVersion: reportVisit.updatedAt }, dispatch);
            if (!response || response.status >= 400) return;
            toast.success('Reporte guardado.');
            setReportDialogVisible(false);
            await refreshReportContext();
        } catch (error) {
            toast.error(getErrorMessage('No pudimos guardar el reporte.'));
        } finally {
            setSavingReportVisitId(null);
        }
    };

    const handleSaveLiveMeeting = useCallback(async (payload, { silent = false } = {}) => {
        if (!reportVisit?._id || savingReportVisitId) return false;
        setSavingReportVisitId(reportVisit._id);
        try {
            const response = await saveLiveMeeting(reportVisit._id, payload, dispatch);
            if (!response || response.status >= 400) return false;
            if (!silent) {
                toast.success('Datos de la reunión guardados.');
                if (reportContext === 'team') {
                    await Promise.all([loadTeamAgenda(), loadTeamOverview()]);
                } else {
                    await Promise.all([loadAgenda(), loadTeamOverview()]);
                }
            }
            return true;
        } catch (error) {
            if (!silent) {
                toast.error(getErrorMessage(error, 'No pudimos guardar los datos de la reunión.'));
            }
            return false;
        } finally {
            setSavingReportVisitId(null);
        }
    }, [
        dispatch,
        loadAgenda,
        loadTeamAgenda,
        loadTeamOverview,
        reportContext,
        reportVisit?._id,
        savingReportVisitId
    ]);

    const handleSubmitVisitReport = async (payload) => {
        if (!reportVisit?._id || savingReportVisitId || !canReportVisit(reportVisit)) return;
        setSavingReportVisitId(reportVisit._id);
        try {
            const saveResponse = await saveVisitReport(reportVisit._id, { ...payload, reportVersion: reportVisit.updatedAt }, dispatch);
            if (!saveResponse || saveResponse.status >= 400) return;
            const submitResponse = await submitVisitReport(reportVisit._id, dispatch);
            if (!submitResponse || submitResponse.status >= 400) return;
            toast.success('Reporte enviado.');
            setReportDialogVisible(false);
            await refreshReportContext();
        } catch (error) {
            toast.error(getErrorMessage('No pudimos enviar el reporte.'));
        } finally {
            setSavingReportVisitId(null);
        }
    };

    const handleStartVisit = (visit) => {
        if (!canStartVisit(visit) || startingVisitId) return;
        const isOffice = visit?.activityType === 'office';
        confirmDialog({
            header: isOffice ? '¿Iniciar tiempo de oficina?' : '¿Empezar visita?',
            message: isOffice
                ? 'Syncro registrará la hora del servidor. Esta actividad no requiere GPS ni reporte comercial.'
                : 'Syncro registrará automáticamente la hora de inicio usando la hora del servidor e intentará capturar tu ubicación actual.',
            acceptLabel: isOffice ? 'Iniciar oficina' : 'Empezar visita',
            rejectLabel: 'Volver',
            acceptClassName: 'p-button-primary',
            accept: async () => {
                setStartingVisitId(visit._id);
                try {
                    if (isOffice) {
                        const response = await executeWithNetworkRetry(
                            () => startOfficeActivity(
                                visit._id,
                                { executionRequestId: createExecutionRequestId() },
                                dispatch
                            ),
                            () => toast.info('Conexión inestable. Reintentando sin duplicar la actividad...')
                        );
                        if (!response || response.status >= 400) return;
                        toast.success('Tiempo de oficina iniciado.');
                        await Promise.all([loadAgenda(), loadTeamOverview()]);
                        return;
                    }
                    const locationPayload = await captureExecutionLocation('start');
                    if (!locationPayload) return;
                    const executionPayload = {
                        ...locationPayload,
                        executionRequestId: createExecutionRequestId()
                    };
                    const response = await executeWithNetworkRetry(
                        () => startVisit(visit._id, executionPayload, dispatch),
                        () => toast.info('Conexión inestable. Reintentando el inicio sin duplicar la visita...')
                    );
                    if (!response || response.status >= 400) return;
                    toast.success(didCaptureLocation(locationPayload)
                        ? 'Visita iniciada con ubicación registrada.'
                        : 'Visita iniciada sin GPS; la justificación quedó registrada.');
                    if (Number(locationPayload?.location?.accuracyMeters || 0) > activeSalesSettings.location.accuracyThresholdMeters) {
                        toast.warn(`La ubicación se guardó con precisión aproximada de ${Math.round(locationPayload.location.accuracyMeters)} m.`);
                    }
                    await Promise.all([loadAgenda(), loadTeamOverview()]);
                } catch (error) {
                    toast.error(getErrorMessage('No pudimos iniciar la visita.'));
                } finally {
                    setStartingVisitId(null);
                }
            }
        });
    };

    const handleFinishVisit = (visit) => {
        if (!canFinishVisit(visit) || finishingVisitId) return;
        const isOffice = visit?.activityType === 'office';
        confirmDialog({
            header: isOffice ? '¿Finalizar tiempo de oficina?' : '¿Finalizar visita?',
            message: isOffice
                ? 'Syncro registrará la hora final del servidor y marcará la actividad como completada. No quedará reporte pendiente.'
                : 'Syncro registrará la hora de finalización con el servidor e intentará capturar la ubicación de salida.',
            acceptLabel: isOffice ? 'Finalizar oficina' : 'Finalizar visita',
            rejectLabel: 'Volver',
            acceptClassName: 'p-button-warning',
            accept: async () => {
                setFinishingVisitId(visit._id);
                try {
                    if (isOffice) {
                        const response = await executeWithNetworkRetry(
                            () => finishOfficeActivity(
                                visit._id,
                                { executionRequestId: createExecutionRequestId() },
                                dispatch
                            ),
                            () => toast.info('Conexión inestable. Reintentando sin duplicar la actividad...')
                        );
                        if (!response || response.status >= 400) return;
                        toast.success('Tiempo de oficina finalizado.');
                        await Promise.all([loadAgenda(), loadTeamOverview()]);
                        return;
                    }
                    const locationPayload = await captureExecutionLocation('finish');
                    if (!locationPayload) return;
                    const executionPayload = {
                        ...locationPayload,
                        executionRequestId: createExecutionRequestId()
                    };
                    const response = await executeWithNetworkRetry(
                        () => finishVisit(visit._id, executionPayload, dispatch),
                        () => toast.info('Conexión inestable. Reintentando la finalización sin duplicar datos...')
                    );
                    if (!response || response.status >= 400) return;
                    toast.success(didCaptureLocation(locationPayload)
                        ? 'Visita finalizada con ubicación registrada. Queda pendiente el reporte.'
                        : 'Visita finalizada sin GPS; la justificación quedó registrada.');
                    if (Number(locationPayload?.location?.accuracyMeters || 0) > activeSalesSettings.location.accuracyThresholdMeters) {
                        toast.warn(`La ubicación final se guardó con precisión aproximada de ${Math.round(locationPayload.location.accuracyMeters)} m.`);
                    }
                    await Promise.all([loadAgenda(), loadTeamOverview()]);
                } catch (error) {
                    toast.error(getErrorMessage('No pudimos finalizar la visita.'));
                } finally {
                    setFinishingVisitId(null);
                }
            }
        });
    };

    const handleStartUnplannedVisit = async (payload) => {
        if (startingUnplannedVisit) return;
        setStartingUnplannedVisit(true);
        try {
            const locationPayload = await captureExecutionLocation('start');
            if (!locationPayload) return;
            const executionPayload = {
                ...payload,
                ...locationPayload,
                executionRequestId: createExecutionRequestId()
            };
            const response = await executeWithNetworkRetry(
                () => startUnplannedVisit(executionPayload, dispatch),
                () => toast.info('Conexión inestable. Reintentando el inicio sin duplicar la visita...')
            );
            if (!response || response.status >= 400) return;
            toast.success(didCaptureLocation(locationPayload)
                ? 'Visita iniciada con ubicación registrada.'
                : 'Visita iniciada sin GPS; la justificación quedó registrada.');
            if (Number(locationPayload?.location?.accuracyMeters || 0) > activeSalesSettings.location.accuracyThresholdMeters) {
                toast.warn(`La ubicación se guardó con precisión aproximada de ${Math.round(locationPayload.location.accuracyMeters)} m.`);
            }
            setUnplannedDialogVisible(false);
            if (!selectedWeekIsCurrent) setSelectedDate(new Date());
            await Promise.all([loadAgenda(), loadTeamOverview()]);
        } catch (error) {
            toast.error(getErrorMessage('No pudimos iniciar la visita no planificada.'));
        } finally {
            setStartingUnplannedVisit(false);
        }
    };

    const handleTeamPrevious = () => {
        setTeamSelectedDate((current) => (teamViewMode === 'day' ? addDays(current, -1) : addWeeks(current, -1)));
    };

    const handleTeamNext = () => {
        setTeamSelectedDate((current) => (teamViewMode === 'day' ? addDays(current, 1) : addWeeks(current, 1)));
    };

    const handleReviewAgenda = async (payload) => {
        if (!canReviewAgendas || !teamAgenda?._id || reviewingAgenda) return;
        setReviewingAgenda(true);
        try {
            const response = await reviewAgenda(teamAgenda._id, payload, dispatch);
            if (!response || response.status >= 400) return;
            toast.success(payload.action === 'returned' ? 'Agenda devuelta al vendedor.' : 'Agenda marcada como revisada.');
            setReviewDialogVisible(false);
            await Promise.all([loadTeamAgenda(), loadTeamOverview(), loadAgenda()]);
        } catch (error) {
            toast.error(getErrorMessage(error, 'No pudimos registrar la revisión.'));
        } finally {
            setReviewingAgenda(false);
        }
    };

    const handleViewAgentAgenda = (agent) => {
        setTeamSelectedAgentId(agent?._id || '');
        setActiveTabIndex(showMyAgenda ? 1 : 0);
    };

    const agentOptionTemplate = (agent) => agent ? (
        <div className="flex flex-column">
            <span>{agent.name || 'Vendedor sin nombre'}</span>
            <small className="text-color-secondary">
                {agent.active ? 'Activo' : 'Inactivo'}
            </small>
        </div>
    ) : null;

    // A supplier-only user has one tab. Do not let null agenda panels leave
    // PrimeReact's original child index selected with an empty content pane.
    // CRM-only users have no agenda tab; keep the first visible panel at index zero.
    if (!canAccessAgenda && canAccessCrm) return (
        <div className="p-4 ventas-module">
            <div className="flex flex-column gap-2 mb-4">
                <h2 className="m-0">Ventas</h2>
                <p className="text-color-secondary mt-0 mb-0">{isSupplierDirectoryOnly(currentAdmin) ? 'Consulta y actualiza los proveedores y sus contactos.' : 'Clientes, contactos y oportunidades comerciales.'}</p>
            </div>
            {notificationPreview}
            <TabView activeIndex={activeTabIndex} onTabChange={(event) => setActiveTabIndex(event.index)} className="ventas-tabs">
                {[
                    <TabPanel key="companies" header="Clientes y contactos"><CrmView mode="companies" /></TabPanel>,
                    !isSupplierDirectoryOnly(currentAdmin) && <TabPanel key="opportunities" header="Oportunidades"><CrmView /></TabPanel>
                ].filter(Boolean)}
            </TabView>
        </div>
    );

    return (
        <div className="p-4 ventas-module">
            <ConfirmDialog />
            <div className="flex flex-column gap-2 mb-4">
                <h2 className="m-0">Ventas</h2>
                <p className="text-color-secondary mt-0 mb-0">Agenda y seguimiento de la gestión comercial en campo.</p>
            </div>

            {notificationPreview}
            <TabView activeIndex={activeTabIndex} onTabChange={(event) => setActiveTabIndex(event.index)} className="ventas-tabs">
                {showMyAgenda ? (
                <TabPanel header={isSeller ? 'Mi agenda' : 'Gestión de agendas'}>
                    <div className="flex flex-column gap-3">
                        {isAdmin ? (
                            <div className="surface-card border-round p-3 shadow-1">
                                <label className="block font-medium mb-2">Gestionar agenda de</label>
                                <Dropdown
                                    value={managedAgentId}
                                    options={agents}
                                    optionLabel="name"
                                    optionValue="_id"
                                    itemTemplate={agentOptionTemplate}
                                    valueTemplate={agentOptionTemplate}
                                    onChange={(event) => setManagedAgentId(event.value || '')}
                                    placeholder="Selecciona un vendedor"
                                    filter
                                    className="w-full"
                                    disabled={loadingAgents}
                                />
                                {managedAgent ? (
                                    <p className="text-sm text-color-secondary mt-2 mb-0">
                                        Gestionando agenda de {managedAgent.name || 'vendedor seleccionado'}.
                                    </p>
                                ) : (
                                    <p className="text-sm text-color-secondary mt-2 mb-0">
                                        Selecciona un vendedor para cargar y gestionar su agenda.
                                    </p>
                                )}
                            </div>
                        ) : null}

                        {isAdmin && !managedAgentId ? (
                            <div className="surface-card border-round p-4 shadow-1 text-center text-color-secondary">
                                Selecciona un vendedor para gestionar su agenda. No se creará una agenda personal para usuarios administrativos.
                            </div>
                        ) : (
                            <AgendaStatusBanner
                                agenda={agenda}
                                activeVisitCount={activeVisitCount}
                                selectedWeekStart={selectedWeekStart}
                                selectedWeekEnd={selectedWeekEnd}
                                isPast={selectedWeekIsPast}
                                isCurrent={selectedWeekIsCurrent}
                                isFuture={selectedWeekIsFuture}
                                isAdmin={isAdmin}
                                canSubmit={canSubmitAgenda}
                                submitting={submittingAgenda}
                                onSubmit={handleSubmitAgenda}
                                deadlineLabel={planningDeadlineLabel}
                            />
                        )}

                        {selectedWeekIsPast ? (
                            <div className="surface-100 border-round p-3 text-color-secondary">
                                Semana cerrada · Reportes e imágenes en modo de solo lectura
                            </div>
                        ) : null}

                        <AgendaHeader
                            idPrefix="ventas-mi-agenda"
                            title={isSeller ? 'Mi agenda' : 'Agenda del vendedor'}
                            viewMode={viewMode}
                            dateLabel={dateLabel}
                            selectedDate={selectedDate}
                            newVisitDisabled={!canCreateVisit}
                            newVisitTooltip={newVisitDisabledReason}
                            onViewModeChange={setViewMode}
                            onDateChange={setSelectedDate}
                            onPrevious={handlePrevious}
                            onNext={handleNext}
                            onToday={handleToday}
                            onNewVisit={handleOpenNewVisit}
                            onNewOffice={() => handleOpenOfficeActivity('planned', 'my')}
                        />

                        {showImmediateActions ? (
                            <div className="surface-card border-round p-3 shadow-1 ventas-unplanned-action">
                                <div>
                                    <h4 className="mt-0 mb-1">Visita no planificada</h4>
                                    <p className="text-color-secondary mt-0 mb-0">Registra una visita inesperada que inicia ahora, aunque estés viendo otra semana. La hora se tomará del servidor y se intentará capturar la ubicación.</p>
                                    {!canStartImmediateActivity ? <small className="text-color-secondary block mt-2">{immediateActivityDisabledReason}</small> : null}
                                </div>
                                <Button
                                    type="button"
                                    label={startingUnplannedVisit ? 'Obteniendo ubicación...' : 'Iniciar visita no planificada'}
                                    icon="pi pi-play"
                                    onClick={() => setUnplannedDialogVisible(true)}
                                    disabled={startingUnplannedVisit || !canStartImmediateActivity}
                                    loading={startingUnplannedVisit}
                                />
                            </div>
                        ) : null}

                        {showImmediateActions ? (
                            <div className="surface-card border-round p-3 shadow-1 ventas-unplanned-action ventas-office-action">
                                <div>
                                    <h4 className="mt-0 mb-1">Tiempo de oficina</h4>
                                    <p className="text-color-secondary mt-0 mb-0">Inicia llamadas, cotizaciones, cobros o trabajo administrativo. Usa la hora del servidor y no solicita GPS ni reporte.</p>
                                    {!canStartImmediateActivity ? <small className="text-color-secondary block mt-2">{immediateActivityDisabledReason}</small> : null}
                                </div>
                                <Button
                                    type="button"
                                    label="Iniciar tiempo de oficina"
                                    icon="pi pi-briefcase"
                                    onClick={() => handleOpenOfficeActivity('startNow', 'my')}
                                    disabled={savingOfficeActivity || !canStartImmediateActivity}
                                    loading={savingOfficeActivity && officeDialogMode === 'startNow'}
                                    outlined
                                />
                            </div>
                        ) : null}

                        {isAdmin && !managedAgentId ? null : loadingAgenda ? (
                            <div className="surface-card border-round p-4 shadow-1 text-center">
                                <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} />
                                <div className="text-color-secondary mt-2">Cargando agenda...</div>
                            </div>
                        ) : viewMode === 'day' ? (
                            <DailyAgendaView
                                selectedDate={selectedDate}
                                visits={visits}
                                canEditVisits={(visit) => canEditPlannedVisit(visit, 'my')}
                                canStartVisit={canStartVisit}
                                canFinishVisit={canFinishVisit}
                                canReportVisit={canReportVisit}
                                canViewReport={canViewReport}
                                isReportLocked={isReportLockedForSettings}
                                weekDays={visibleWeekDays}
                                actionLoadingVisitId={startingVisitId || finishingVisitId || savingReportVisitId}
                                locationLoadingVisitId={startingVisitId}
                                onEditVisit={handleEditVisit}
                                onCancelVisit={handleOpenCancelVisit}
                                onStartVisit={handleStartVisit}
                                onFinishVisit={handleFinishVisit}
                                onReportVisit={(visit) => handleOpenReport(visit, 'my')}
                            />
                        ) : (
                            <WeeklyAgendaView
                                selectedDate={selectedDate}
                                visits={visits}
                                canEditVisits={(visit) => canEditPlannedVisit(visit, 'my')}
                                canStartVisit={canStartVisit}
                                canFinishVisit={canFinishVisit}
                                canReportVisit={canReportVisit}
                                canViewReport={canViewReport}
                                isReportLocked={isReportLockedForSettings}
                                weekDays={visibleWeekDays}
                                actionLoadingVisitId={startingVisitId || finishingVisitId || savingReportVisitId}
                                locationLoadingVisitId={startingVisitId}
                                onEditVisit={handleEditVisit}
                                onCancelVisit={handleOpenCancelVisit}
                                onStartVisit={handleStartVisit}
                                onFinishVisit={handleFinishVisit}
                                onReportVisit={(visit) => handleOpenReport(visit, 'my')}
                            />
                        )}
                    </div>
                </TabPanel>
                ) : null}

                {canViewTeamAgenda ? (
                    <TabPanel header="Agenda del equipo">
                        <TeamAgendaView
                            agents={teamAgents}
                            selectedAgentId={teamSelectedAgentId}
                            onAgentChange={setTeamSelectedAgentId}
                            agenda={teamAgenda}
                            visits={teamVisits}
                            overview={teamOverview}
                            loading={loadingTeamAgenda || loadingTeamAgents}
                            loadingOverview={loadingTeamOverview}
                            viewMode={teamViewMode}
                            dateLabel={teamDateLabel}
                            selectedDate={teamSelectedDate}
                            selectedWeekStart={teamSelectedWeekStart}
                            selectedWeekEnd={teamSelectedWeekEnd}
                            isPast={teamWeekIsPast}
                            isCurrent={teamWeekIsCurrent}
                            isFuture={teamWeekIsFuture}
                            activeVisitCount={teamActiveVisitCount}
                            canShowCreateVisit={isAdmin}
                            canCreateVisit={canManageTeamWeek}
                            canEditVisits={(visit) => canEditPlannedVisit(visit, 'team')}
                            savingVisit={savingVisit}
                            submittingAgenda={submittingAgenda}
                            canSubmitAgenda={false}
                            onSubmitAgenda={() => {}}
                            onViewModeChange={setTeamViewMode}
                            onDateChange={setTeamSelectedDate}
                            onPrevious={handleTeamPrevious}
                            onNext={handleTeamNext}
                            onToday={() => setTeamSelectedDate(getPanamaToday())}
                            onNewVisit={handleOpenTeamNewVisit}
                            onNewOffice={() => handleOpenOfficeActivity('planned', 'team')}
                            onEditVisit={(visit) => handleEditVisit(visit, 'team')}
                            onCancelVisit={(visit) => handleOpenCancelVisit(visit, 'team')}
                            canViewReport={(visit) => canViewAllReports && canViewReport(visit)}
                            isReportLocked={isReportLockedForSettings}
                            onReportVisit={(visit) => handleOpenReport(visit, 'team')}
                            canReviewAgenda={Boolean(canReviewAgendas && teamAgenda?.status === 'submitted' && teamAgenda?.reviewStatus !== 'reviewed')}
                            onReviewAgenda={() => setReviewDialogVisible(true)}
                            weekDays={teamVisibleWeekDays}
                            deadlineLabel={planningDeadlineLabel}
                            settings={activeSalesSettings}
                        />
                    </TabPanel>
                ) : null}

                {canViewAgents ? (
                    <TabPanel header="Vendedores">
                        <SalesAgentsView
                            agents={agents}
                            loading={loadingAgents}
                            canManageSellers={canManageSellers}
                            deletingSellerId={deletingSellerId}
                            currentUserId={currentUserId}
                            onAddSeller={handleOpenSellerDialog}
                            onDeleteSeller={handleDeleteSeller}
                            onViewAgenda={handleViewAgentAgenda}
                        />
                    </TabPanel>
                ) : null}

                {canManageSettings ? (
                    <TabPanel header="Ajustes">
                        <SalesSettingsView
                            settingsState={settingsState}
                            loading={loadingSettings}
                            saving={savingSettings}
                            onReload={loadSettings}
                            onSave={handleSaveSettings}
                            onReset={handleResetSettings}
                        />
                    </TabPanel>
                ) : null}
                {canAccessCrm && !isSupplierDirectoryOnly(currentAdmin) && (
                    <TabPanel header="Oportunidades"><CrmView /></TabPanel>
                )}
                {canAccessCrm && (
                    <TabPanel header="Clientes y contactos"><CrmView mode="companies" /></TabPanel>
                )}
            </TabView>

            <AddSellerDialog
                visible={sellerDialogVisible}
                candidates={sellerCandidates}
                loadingCandidates={loadingSellerCandidates}
                assigning={assigningSeller}
                onHide={handleCloseSellerDialog}
                onRefreshCandidates={loadSellerCandidates}
                onAssign={handleAssignSeller}
                onCreateInUsers={handleCreateSellerInUsers}
            />

            <AgendaReviewDialog
                visible={reviewDialogVisible}
                agenda={teamAgenda}
                loading={reviewingAgenda}
                onHide={() => {
                    if (reviewingAgenda) return;
                    setReviewDialogVisible(false);
                }}
                onConfirm={handleReviewAgenda}
            />

            <VisitDialog
                visible={visitDialogVisible}
                selectedDate={visitContext === 'team' ? teamSelectedDate : selectedDate}
                weekStartDate={visitContext === 'team' ? teamSelectedWeekStart : selectedWeekStart}
                weekDays={visitContext === 'team' ? teamVisibleWeekDays : visibleWeekDays}
                visit={editingVisit}
                customers={customers}
                loadingCustomers={loadingCustomers}
                customerLoadError={customerLoadError}
                onReloadCustomers={loadCustomers}
                loading={savingVisit}
                onHide={handleCloseVisitDialog}
                onSubmit={handleSaveVisit}
            />

            <OfficeActivityDialog
                visible={officeDialogVisible}
                mode={officeDialogMode}
                selectedDate={visitContext === 'team' ? teamSelectedDate : selectedDate}
                weekDays={visitContext === 'team' ? teamVisibleWeekDays : visibleWeekDays}
                activity={editingOfficeActivity}
                loading={savingOfficeActivity}
                onHide={handleCloseOfficeDialog}
                onSubmit={handleSaveOfficeActivity}
            />

            <CancelVisitDialog
                visible={cancelDialogVisible}
                visit={visitToCancel}
                cancelling={cancellingVisit}
                onHide={() => {
                    if (cancellingVisit) return;
                    setCancelDialogVisible(false);
                    setVisitToCancel(null);
                }}
                onConfirm={handleConfirmCancelVisit}
            />

            <ReportVisitDialog
                visible={reportDialogVisible}
                visit={reportVisit}
                readOnly={reportContext === 'team' || !canReportVisit(reportVisit)}
                locked={reportVisit ? isReportLockedForSettings(reportVisit) : false}
                settings={activeSalesSettings}
                loading={loadingReport || savingReportVisitId === reportVisit?._id}
                onHide={handleCloseReportDialog}
                onSaveDraft={handleSaveReportDraft}
                onSubmitReport={handleSubmitVisitReport}
                onLiveSave={handleSaveLiveMeeting}
            />

            <LocationCaptureDialog
                decision={locationDecision}
                onCancel={() => resolveLocationDecision({ action: 'cancel' })}
                onRetry={() => resolveLocationDecision({ action: 'retry' })}
                onContinue={(note) => resolveLocationDecision({
                    action: 'continue',
                    note
                })}
            />

            <UnplannedVisitDialog
                visible={unplannedDialogVisible}
                customers={customers}
                loadingCustomers={loadingCustomers}
                customerLoadError={customerLoadError}
                onReloadCustomers={loadCustomers}
                loading={startingUnplannedVisit}
                onHide={handleCloseUnplannedDialog}
                onSubmit={handleStartUnplannedVisit}
            />
        </div>
    );
};

export default VentasModule;
